import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Users, Trophy } from "@phosphor-icons/react";
import { useFavorites } from "../contexts/FavoritesContext";
import { useSportsData } from "../contexts/SportsDataContext";
import { Star } from "@phosphor-icons/react";
import GameCard from "../components/GameCard";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamProfile() {
  const { leagueKey, teamId } = useParams();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { fetchLeagueScores } = useSportsData();
  const [team, setTeam] = useState(null);
  const [teamGames, setTeamGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueKey || !teamId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/teams/${leagueKey}`).then((r) => {
        const t = (r.data.teams || []).find((t) => t.id === teamId);
        setTeam(t || null);
      }).catch(() => {}),
      fetchLeagueScores(leagueKey).then((games) => {
        const filtered = games.filter(
          (g) => g.homeTeam.id === teamId || g.awayTeam.id === teamId
        );
        setTeamGames(filtered);
      }),
    ]).finally(() => setLoading(false));
  }, [leagueKey, teamId, fetchLeagueScores]);

  if (loading) return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6">
      <div className="h-48 bg-[#141414] rounded-lg animate-pulse" />
    </div>
  );

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="team-profile">
      {/* Back nav */}
      <div className="flex items-center gap-3">
        <Link to={`/league/${leagueKey}`} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="team-back-btn">
          <ArrowLeft size={16} className="text-white/70" />
        </Link>
        <span className="text-xs tracking-widest uppercase text-[#007AFF] font-bold">{leagueKey?.toUpperCase()}</span>
        <button onClick={() => toggleFavorite(`team-${teamId}`)} className="ml-auto p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="team-favorite-btn">
          <Star size={16} weight={isFavorite(`team-${teamId}`) ? "fill" : "regular"} className={isFavorite(`team-${teamId}`) ? "text-[#007AFF]" : "text-white/40"} />
        </button>
      </div>

      {/* Team header */}
      {team ? (
        <div className="bg-[#141414] border border-white/10 rounded-xl p-6 flex items-center gap-6" data-testid="team-header"
          style={team.color ? { borderColor: `#${team.color}40` } : {}}
        >
          {team.logo ? (
            <img src={team.logo} alt={team.name} className="w-20 h-20 object-contain" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center text-2xl font-bold"
              style={team.color ? { backgroundColor: `#${team.color}20`, color: `#${team.color}` } : {}}
            >
              {team.abbreviation?.slice(0, 2)}
            </div>
          )}
          <div>
            <h1 className="font-['Bebas_Neue'] text-3xl sm:text-4xl tracking-tighter uppercase text-white" data-testid="team-name">
              {team.displayName || team.name}
            </h1>
            <p className="text-sm text-white/40 flex items-center gap-2 mt-1">
              <Users size={14} /> {team.location}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-[#141414] border border-white/10 rounded-xl p-6 text-center">
          <p className="text-white/40 text-sm">Team info not available</p>
        </div>
      )}

      {/* Team games */}
      <div>
        <h2 className="font-['Bebas_Neue'] text-xl uppercase tracking-wider text-white mb-3 flex items-center gap-2">
          <Trophy size={18} weight="fill" className="text-[#007AFF]" /> Games
        </h2>
        {teamGames.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger-children" data-testid="team-games-grid">
            {teamGames.map((g) => <GameCard key={g.id} game={g} />)}
          </div>
        ) : (
          <p className="text-center py-8 text-white/30 text-sm">No games found for this team</p>
        )}
      </div>
    </div>
  );
}
